import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap } from "rxjs";
import { BaseRespone, PaginationResult } from "src/app/models/common.response";
import { environment } from "src/environments/environment";
import { PaginationRequest } from "src/app/models/common.request";
import { HospitalSummary, IHospitalInfo } from "./ihospital-info";
import { TranslateService } from "@ngx-translate/core";


export interface MigrateLegacyDocumentsResult {
    legacyRowsFound: number;
    migrated: number;
    missingFile: number;
    failed: number;
    errors: string[];
}


export interface WhatsAppMessageCount {
    totalMessages: number;
    usedMessages: number;
    remainingMessages: number;
    isLimitReached: boolean;
}

@Injectable({ providedIn: 'root' })
export class HospitalService {

    private _hospital: BehaviorSubject<IHospitalInfo | null> = new BehaviorSubject<IHospitalInfo | null>(null);
    private _hospitals: BehaviorSubject<IHospitalInfo[]> = new BehaviorSubject<IHospitalInfo[]>([]);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);

    constructor(
        private _httpClient: HttpClient,
        private _translate: TranslateService
    ) { }

    get hospital$(): Observable<IHospitalInfo | null> {
        return this._hospital.asObservable();
    }

    get hospitals$(): Observable<IHospitalInfo[]> {
        return this._hospitals.asObservable();
    }

    get pagination$(): Observable<PaginationResult | null> {
        return this._pagination.asObservable();
    }


    updateHospital(hospital: FormData): Observable<BaseRespone<IHospitalInfo>> {
        return this.hospitals$.pipe(
            take(1),
            switchMap(hospitals => this._httpClient.put<BaseRespone<IHospitalInfo>>(`${environment.baseURl}/Hospital`, hospital).pipe(
                map((updated: BaseRespone<IHospitalInfo>) => {
                    const hospitalid = hospital.get('id');
                    const index = hospitals.findIndex(item => item.id === Number(hospitalid));

                    if (index !== -1) {
                        hospitals[index] = { ...hospitals[index], ...updated.result };
                        this._hospitals.next(hospitals);
                    }

                    // Return the updated product
                    return updated;
                })
            )), catchError((error) => {
                return of(error.error);
            })
        );
    }

    updatePlatformHospital(id: number, hospital: FormData): Observable<BaseRespone<IHospitalInfo>> {
        return this._httpClient.put<BaseRespone<IHospitalInfo>>(`${environment.baseURl}/Hospital/${id}`, hospital);
    }

    updateHospitalAddOns(id: number, ipd: boolean | null, pharmacy: boolean | null, whatsApp: boolean | null): Observable<BaseRespone<IHospitalInfo>> {
        return this._httpClient.put<BaseRespone<IHospitalInfo>>(`${environment.baseURl}/Hospital/${id}/add-ons`, { ipd: ipd === true, pharmacy: pharmacy === true, whatsApp: whatsApp === true });
    }

    getHospital(hospitaldata: PaginationRequest): Observable<BaseRespone<{ pagination: PaginationResult; hospitals: IHospitalInfo[]; summary?: HospitalSummary }>> {
        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; hospitals: IHospitalInfo[]; summary?: HospitalSummary }>>(`${environment.baseURl}/Hospital/list`, hospitaldata).pipe(
            tap((response) => {
                this._pagination.next(response.result.pagination);
                this._hospitals.next(response.result.hospitals);
            }),
            catchError((error) => {
                console.error('Error to Fetching data:', error);
                throw error;
            })
        );
    }

    createHospital(hospital: FormData): Observable<BaseRespone<IHospitalInfo>> {
        return this._httpClient.post<BaseRespone<IHospitalInfo>>(`${environment.baseURl}/Hospital`, hospital)
            .pipe(
                catchError((error) => {
                    if (error instanceof HttpErrorResponse && error.status === 429) {
                        return of(this.getTooManySignupAttemptsResponse());
                    }

                    return of(error.error);
                }),
                map((newhospital) => {
                    return newhospital;
                })
            )
    }

    createHospitalWithUser(hospital: FormData): Observable<BaseRespone<IHospitalInfo>> {
        return this._httpClient.post<BaseRespone<IHospitalInfo>>(`${environment.baseURl}/Hospital/hospitalUser`, hospital)
            .pipe(
                catchError((error) => {
                    if (error instanceof HttpErrorResponse && error.status === 429) {
                        return of(this.getTooManySignupAttemptsResponse());
                    }

                    return of(error.error);
                }),
                map((newhospital) => {
                    return newhospital;
                })
            )
    }

    migrateLegacyDocuments(hospitalId: number): Observable<BaseRespone<MigrateLegacyDocumentsResult>> {
        return this._httpClient.post<BaseRespone<MigrateLegacyDocumentsResult>>(
            `${environment.baseURl}/Hospital/${hospitalId}/migrate-legacy-documents`,
            null
        );
    }

    getWhatsAppMessageCount(): Observable<BaseRespone<WhatsAppMessageCount>> {
        return this._httpClient.get<BaseRespone<WhatsAppMessageCount>>(`${environment.baseURl}/WhatsApp/message-count`);
    }

    private getTooManySignupAttemptsResponse(): BaseRespone<IHospitalInfo> {
        const message = this._translate.instant('COMMON.TOO_MANY_SIGNUP_ATTEMPTS');
        return {
            isSuccess: false,
            message,
            errors: [message],
            result: null as unknown as IHospitalInfo
        };
    }
}
